import { Component, forwardRef, OnInit, signal, WritableSignal } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { RADIO_PLAN_DESC } from '@app/core/models/Radio';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { PlaylistCriteriaDirective } from '@app/library/components/sides/playlist-criteria/playlist-criteria.directive';
import { Criterion, operators } from '@app/library/models/criterias.model';
import { BacsState } from '@app/library/states/bacs.state';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { Select, Store } from '@ngxs/store';
import { FormHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

const boxOperators = [
  {
    id: operators.EQUALS,
    name: operators.EQUALS,
    translation: marker('PLAYLIST.CRITERIA.EQUALS'),
  },
  {
    id: operators.NOT_EQUALS,
    name: operators.NOT_EQUALS,
    translation: marker('PLAYLIST.CRITERIA.NOT_EQUALS'),
  },
];

@Component({
  selector: 'rk-playlist-criteria-box',
  templateUrl: './playlist-criteria-box.component.html',
  styleUrls: ['./playlist-criteria-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlaylistCriteriaBoxComponent),
      multi: true,
    },
  ],
})
export class PlaylistCriteriaBoxComponent
  extends PlaylistCriteriaDirective
  implements OnInit, ControlValueAccessor
{
  @Select(BacsState.allBoxesForSelect)
  boxes$: Observable<SugestionInput[]>;

  @Select(LiveTrackingState.planDesc)
  planDesc$: Observable<RADIO_PLAN_DESC>;

  $boxes: WritableSignal<SugestionInput[]> = signal(null);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly store: Store,
    formHelper: FormHelperService,
  ) {
    super(formHelper);
  }

  ngOnInit() {
    super.ngOnInit();
    this.subscription.add(
      combineLatest([this.boxes$, this.planDesc$]).subscribe(([boxes, plan]) => {
        this.$boxes.set(
          ['Discover', 'Demo'].indexOf(plan) > -1
            ? boxes.filter(b => ['__DEDICATION__', '__CHRONIC__'].indexOf(b.name) < 0)
            : boxes,
        );
      }),
    );
    this.criteriaForm = this.fb.group({
      field: '',
      operator: boxOperators[0],
      value: '',
      chipsValue: [[]],
    });
  }

  isTextInput() {
    return this.criteriaForm.get('operator').value.name === operators.NOT_EQUALS;
  }

  generateOperatorsInput(): SugestionInput[] {
    return boxOperators;
  }

  formValueToCriteria(formData: any): Criterion {
    const shouldReset = this.prevOperator['id'] !== formData.operator['id'];
    this.prevOperator = formData.operator;
    const firstBox = this.store.selectSnapshot(BacsState.firstBox);
    if (shouldReset) {
      this.criteriaForm.clearValidators();
      this.criteriaForm.get('value').setValue(firstBox);
      this.criteriaForm.get('chipsValue').setValue([]);
    }

    return {
      field: formData.field,
      operator: formData.operator['id'],
      value:
        shouldReset && this.isTextInput()
          ? firstBox.id
          : this.isTextInput()
            ? formData.value.id
            : formData.chipsValue.map((chip: any) => chip.id),
    };
  }

  async criteriaToFormValue(val: any): Promise<any> {
    const ret = {
      ...val,
      operator: this.findOperatorWithId(val.operator),
      chipsValue: [],
    };
    this.prevOperator = ret.operator;

    if (val.value instanceof Array) {
      const promises = val.value.map(async (chip: number) => {
        const bacName = await this.store
          .selectSnapshot(BacsState.bacById)(chip)
          .translated$.pipe(take(1))
          .toPromise();

        return { id: chip, name: bacName };
      });
      ret['chipsValue'] = await Promise.all(promises);
      ret['value'] = '';
    } else if (val.value) {
      const bacName = await this.store
        .selectSnapshot(BacsState.bacById)(val.value)
        .translated$.pipe(take(1))
        .toPromise();
      ret['value'] = { id: val.value, name: bacName };
    }

    return ret;
  }

  findOperatorWithId(name: string) {
    return boxOperators.find(operator => operator.id === name);
  }

  registerOnChange(fn: any): void {
    this.criteriaForm.valueChanges.subscribe(val => fn(this.formValueToCriteria(val)));
  }

  writeValue(value: any): void {
    if (!value) {
      return;
    }
    this.criteriaToFormValue(value).then(val =>
      this.criteriaForm.setValue(val, { emitEvent: false }),
    );
  }
}
